import { useState, useEffect } from "react";
import api from "../../../core/axios/axiosInstance";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";
import { useDispatch } from "react-redux";

const InvoiceCustomization = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [fetchingSettings, setFetchingSettings] = useState(true);

  const [businessName, setBusinessName] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [supportPhone, setSupportPhone] = useState("");
  const [supportUrl, setSupportUrl] = useState("");
  const [businessUrl, setBusinessUrl] = useState("");

  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");

  const [defaultFooter, setDefaultFooter] = useState("");
  const [accountName, setAccountName] = useState("");

  const [primaryColor, setPrimaryColor] = useState("#000000");
  const [secondaryColor, setSecondaryColor] = useState("#ffffff");

  const fetchInvoiceSettings = async () => {
    try {
      setFetchingSettings(true);
      const response = await api.get("/admin/invoice-customization/settings");

      if (response.data.success) {
        const { branding, invoice, business, account_name } =
          response.data.data;

        setBusinessName(business.name || "");
        setSupportEmail(business.support_email || "");
        setSupportPhone(business.support_phone || "");
        setSupportUrl(business.support_url || "");
        setBusinessUrl(business.url || "");

        if (business.support_address) {
          setAddressLine1(business.support_address.line1 || "");
          setAddressLine2(business.support_address.line2 || "");
          setCity(business.support_address.city || "");
          setState(business.support_address.state || "");
          setPostalCode(business.support_address.postal_code || "");
          setCountry(business.support_address.country || "");
        }

        setDefaultFooter(invoice.default_footer || "");
        setAccountName(account_name || "");

        setPrimaryColor(branding.primary_color || "#000000");
        setSecondaryColor(branding.secondary_color || "#ffffff");
      }
    } catch (error) {
      console.error("Error fetching invoice settings:", error);
      dispatch(
        showToast({
          message:
            error.response?.data?.message || "Failed to load invoice settings",
          variant: "danger",
        })
      );
    } finally {
      setFetchingSettings(false);
    }
  };

  useEffect(() => {
    fetchInvoiceSettings();
  }, []);

  const handleUpdateBusinessProfile = async () => {
    try {
      setLoading(true);
      const response = await api.put(
        "/admin/invoice-customization/business-profile",
        {
          name: businessName,
          support_email: supportEmail,
          support_phone: supportPhone,
          support_url: supportUrl,
          url: businessUrl,
          support_address: {
            line1: addressLine1,
            line2: addressLine2,
            city: city,
            state: state,
            postal_code: postalCode,
            country: country,
          },
        }
      );

      if (response.data.success) {
        dispatch(
          showToast({
            message: "Business profile updated successfully",
            variant: "success",
          })
        );
      }
    } catch (error) {
      console.error("Error updating business profile:", error);
      dispatch(
        showToast({
          message:
            error.response?.data?.message ||
            "Failed to update business profile",
          variant: "danger",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFooter = async () => {
    try {
      setLoading(true);
      const response = await api.put("/admin/invoice-customization/footer", {
        footer: defaultFooter,
      });

      if (response.data.success) {
        dispatch(
          showToast({
            message: "Default footer updated successfully",
            variant: "success",
          })
        );
      }
    } catch (error) {
      console.error("Error updating footer:", error);
      dispatch(
        showToast({
          message: error.response?.data?.message || "Failed to update footer",
          variant: "danger",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBranding = async () => {
    try {
      setLoading(true);
      const response = await api.put("/admin/invoice-customization/branding", {
        primary_color: primaryColor,
        secondary_color: secondaryColor,
      });

      if (response.data.success) {
        dispatch(
          showToast({
            message: "Branding colors updated successfully",
            variant: "success",
          })
        );
      }
    } catch (error) {
      console.error("Error updating branding:", error);
      dispatch(
        showToast({
          message: error.response?.data?.message || "Failed to update branding",
          variant: "danger",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  if (fetchingSettings) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col">
                  <h4 className="page-title">Invoice Customization</h4>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">Business Profile</h5>
                <p className="text-muted small mb-0">
                  This information appears on your invoices
                </p>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Business Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="Enter business name"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Account Display Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={accountName}
                      onChange={(e) => setAccountName(e.target.value)}
                      placeholder="Enter account display name"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Support Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={supportEmail}
                      onChange={(e) => setSupportEmail(e.target.value)}
                      placeholder="support@example.com"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Support Phone</label>
                    <input
                      type="tel"
                      className="form-control"
                      value={supportPhone}
                      onChange={(e) => setSupportPhone(e.target.value)}
                      placeholder="+1234567890"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Business Website</label>
                    <input
                      type="url"
                      className="form-control"
                      value={businessUrl}
                      onChange={(e) => setBusinessUrl(e.target.value)}
                      placeholder="https://example.com"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Support URL</label>
                    <input
                      type="url"
                      className="form-control"
                      value={supportUrl}
                      onChange={(e) => setSupportUrl(e.target.value)}
                      placeholder="https://support.example.com"
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <h6 className="mb-3 mt-3">Support Address</h6>
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Address Line 1</label>
                    <input
                      type="text"
                      className="form-control"
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      placeholder="Street address"
                    />
                  </div>

                  <div className="col-md-12 mb-3">
                    <label className="form-label">Address Line 2</label>
                    <input
                      type="text"
                      className="form-control"
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder="Apartment, suite, etc. (optional)"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-control"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="City"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">State/Province</label>
                    <input
                      type="text"
                      className="form-control"
                      value={state}
                      onChange={(e) => setState(e.target.value)}
                      placeholder="State"
                    />
                  </div>

                  <div className="col-md-4 mb-3">
                    <label className="form-label">Postal Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder="Postal code"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Country</label>
                    <input
                      type="text"
                      className="form-control"
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      placeholder="Country code (e.g., US)"
                      maxLength={2}
                    />
                    <small className="text-muted">
                      2-letter ISO country code
                    </small>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-12">
                    <button
                      className="btn btn-primary"
                      onClick={handleUpdateBusinessProfile}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update Business Profile"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">Branding & Colors</h5>
                <p className="text-muted small mb-0">
                  Customize the colors used in your invoices and receipts
                </p>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Primary Color (Brand Color)
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        style={{ width: "60px", height: "40px" }}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        placeholder="#000000"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                    </div>
                    <small className="text-muted">
                      Used on receipts, invoices, and customer portal
                    </small>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Secondary Color (Accent Color)
                    </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="color"
                        className="form-control form-control-color"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        style={{ width: "60px", height: "40px" }}
                      />
                      <input
                        type="text"
                        className="form-control"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        placeholder="#ffffff"
                        pattern="^#[0-9A-Fa-f]{6}$"
                      />
                    </div>
                    <small className="text-muted">
                      Used as background on emails and pages
                    </small>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-12">
                    <button
                      className="btn btn-primary"
                      onClick={handleUpdateBranding}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update Branding"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header">
                <h5 className="card-title mb-0">Invoice Settings</h5>
                <p className="text-muted small mb-0">
                  Configure default invoice content
                </p>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Default Footer</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={defaultFooter}
                      onChange={(e) => setDefaultFooter(e.target.value)}
                      placeholder="Enter default footer text that appears on all invoices (e.g., company registration info, terms, etc.)"
                    />
                    <small className="text-muted">
                      This footer will appear at the bottom of all invoice PDFs.
                      Commonly used for legal information, registration numbers,
                      or terms.
                    </small>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-12">
                    <button
                      className="btn btn-primary"
                      onClick={handleUpdateFooter}
                      disabled={loading}
                    >
                      {loading ? "Updating..." : "Update Invoice Settings"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-info">
              <h6 className="alert-heading">
                <i className="fas fa-info-circle me-2"></i>
                Important Information
              </h6>
              <ul className="mb-0">
                <li>All changes are saved directly to your Stripe account</li>
                <li>
                  Updates apply to all future invoices generated for your
                  customers
                </li>
                <li>Existing invoices will not be affected by these changes</li>
                <li>Colors must be in hexadecimal format (e.g., #FF5733)</li>
                <li>
                  Country code must be a 2-letter ISO code (e.g., US, GB, AE)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceCustomization;
